"use client";

import type { PostHog } from "posthog-js";
import { hasConsent, isConsentDenied, onConsentChange } from "@/lib/consent";

/**
 * Product analytics on the landing page (user-retention step 05). Same
 * PostHog project as the dashboard, so a visitor who consents on both sites
 * is one person from the first landing pageview to their first match view.
 *
 * The link is the shared `ph_<key>_posthog` cookie on `.football-genie.com`
 * (cross_subdomain_cookie). cookieWinsOnConflict makes that cookie beat the
 * per-origin localStorage copy, so an identify on the dashboard reaches this
 * site too.
 *
 * Like the dashboard: nothing loads before "analytics" consent (no script, no
 * request, no storage); a reject or withdrawal opts out and clears storage.
 */

let client: PostHog | null = null;
let loading: Promise<PostHog | null> | null = null;

const key = () => process.env.NEXT_PUBLIC_POSTHOG_KEY;

const load = (): Promise<PostHog | null> => {
  loading ??= import("posthog-js").then(({ default: posthog }) => {
    posthog.init(key()!, {
      api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST || "/ingest",
      ui_host: "https://eu.posthog.com",
      defaults: "2026-01-30",
      person_profiles: "identified_only",
      persistence: "localStorage+cookie",
      cross_subdomain_cookie: true,
      cookieWinsOnConflict: true,
      disable_session_recording: true,
    });
    posthog.register({ environment: process.env.NODE_ENV, site: "landing" });
    client = posthog;
    return posthog;
  });
  return loading;
};

// PostHog keeps its ids in a `ph_<key>_posthog` cookie and the same key in
// localStorage. reset() only swaps in fresh ids, so after a reject the stored
// identifier has to be deleted by hand.
const isPosthogKey = (name: string) => /^(ph_|__ph_)/.test(name);

const clearStoredIdentity = () => {
  const host = window.location.hostname;
  const parts = host.split(".");
  // cross_subdomain_cookie writes to the registrable domain (.example.com).
  const domains = ["", host, `.${host}`];
  if (parts.length >= 2) domains.push(`.${parts.slice(-2).join(".")}`);
  document.cookie
    .split("; ")
    .map((pair) => pair.split("=")[0])
    .filter(isPosthogKey)
    .forEach((name) =>
      domains.forEach((domain) => {
        document.cookie = `${name}=; Max-Age=0; path=/${domain ? `; domain=${domain}` : ""}`;
      }),
    );
  for (const storage of [window.localStorage, window.sessionStorage]) {
    try {
      Object.keys(storage)
        .filter(isPosthogKey)
        .forEach((k) => storage.removeItem(k));
    } catch {
      // Storage can throw in private modes; nothing to clear then.
    }
  }
};

const sync = () => {
  if (hasConsent("analytics")) {
    void load().then((posthog) => {
      if (!posthog) return;
      posthog.set_config({ persistence: "localStorage+cookie" });
      if (posthog.has_opted_out_capturing()) posthog.opt_in_capturing();
    });
  } else if (isConsentDenied("analytics")) {
    // Not done while CookieYes is still loading, or every page load of a
    // consenting visitor would wipe their ids.
    client?.opt_out_capturing();
    client?.reset();
    client?.set_config({ persistence: "memory" });
    clearStoredIdentity();
  }
};

const dashboardHost = () => {
  try {
    return new URL(process.env.NEXT_PUBLIC_APP_URL || "").host;
  } catch {
    return null;
  }
};

/** The funnel's middle step: a click on any link into the dashboard. */
export const trackCta = (cta: string, href?: string) => {
  if (!client || !hasConsent("analytics")) return;
  // The page navigates away right after; send now, by beacon.
  client.capture(
    "cta_clicked",
    { cta, href },
    { send_instantly: true, transport: "sendBeacon" },
  );
};

// One listener instead of a call in every CTA component: catches the hero,
// nav, pricing and plain FAQ links alike.
const onClick = (event: MouseEvent) => {
  const host = dashboardHost();
  const link = (event.target as Element | null)?.closest?.("a[href]");
  if (!host || !(link instanceof HTMLAnchorElement)) return;
  let url: URL;
  try {
    url = new URL(link.href);
  } catch {
    return;
  }
  if (url.host !== host) return;
  const section =
    link.closest("section")?.getAttribute("aria-labelledby") ??
    (link.closest("nav, header") ? "navigation" : "page");
  const label = (link.getAttribute("aria-label") || link.textContent || "")
    .trim()
    .slice(0, 80);
  trackCta(`${section}: ${label}`, url.pathname);
};

/** Mounted once, from Providers. Returns the unsubscribe. */
export const startAnalytics = (): (() => void) => {
  if (!key()) return () => undefined;
  sync();
  const stopConsent = onConsentChange(sync);
  document.addEventListener("click", onClick, { capture: true });
  return () => {
    stopConsent();
    document.removeEventListener("click", onClick, { capture: true });
  };
};
